import { USER_ROLE } from "Constants/constant";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import { groupByPermissionsByName } from "Utils/utils";
import { isGhostSessionActive, readGhostSession } from "context/platformAdminContext.utils";

export const MIN_GHOST_REASON_LENGTH = 10;

export const isGhostReasonValid = (reason) =>
  typeof reason === "string" && reason.trim().length >= MIN_GHOST_REASON_LENGTH;

export const canGhostUser = (user) =>
  Boolean(user?.id) &&
  user.user_type !== USER_ROLE.Super_Admin &&
  user.user_type !== "super_admin";

export const isSpAdminUserType = (userType) =>
  userType === USER_ROLE.Sp_Admin || userType === "sp_admin";

export const applySessionUserDisplayRules = (user) => {
  if (!user) {
    return null;
  }

  const sessionUser = { ...user };
  const userType = sessionUser.user_type || sessionUser.userType;

  if (isSpAdminUserType(userType) && sessionUser?.service_provider?.name) {
    sessionUser.name = sessionUser.service_provider.name;
  }

  return sessionUser;
};

const resolveActingServiceProvider = (actingUser, ghostSession) => {
  if (actingUser?.service_provider) {
    return actingUser.service_provider;
  }

  if (ghostSession?.spName) {
    return {
      id: ghostSession.spId ?? null,
      name: ghostSession.spName,
    };
  }

  return null;
};

const resolveActingPermissions = (actingUser) => {
  const permissions = actingUser?.permissions;
  if (!permissions || typeof permissions !== "object") {
    return undefined;
  }

  return Object.keys(permissions).length > 0 ? permissions : undefined;
};

/**
 * Identity used across tenant UI during ghost: acting user fields, not platform staff.
 */
export const getSessionUser = () => {
  const storedUser = GetItemFromLocalstorage("user");
  if (!storedUser) {
    return null;
  }

  if (!isGhostSessionActive()) {
    return storedUser;
  }

  const ghostSession = readGhostSession();
  const actingUser = ghostSession?.actingUser;
  if (!actingUser) {
    return storedUser;
  }

  const {
    platform_permissions: _platformPermissions,
    platform_roles: _platformRoles,
    service_provider: _serviceProvider,
    profile_picture: _profilePicture,
    permissions: _storedPermissions,
    ...platformShell
  } = storedUser;

  const serviceProvider = resolveActingServiceProvider(actingUser, ghostSession);
  const permissions = resolveActingPermissions(actingUser);
  const sessionUser = {
    ...platformShell,
    id: actingUser.id ?? storedUser.id,
    uuid: actingUser.uuid ?? storedUser.uuid,
    name: actingUser.name ?? storedUser.name,
    email: actingUser.email ?? storedUser.email,
    first_name: actingUser.first_name ?? storedUser.first_name,
    last_name: actingUser.last_name ?? storedUser.last_name,
    user_type: actingUser.user_type ?? storedUser.user_type,
    userType: actingUser.user_type ?? storedUser.userType,
    role_id: actingUser.role_id ?? storedUser.role_id,
    profile_picture: actingUser.profile_picture ?? null,
    ...(serviceProvider ? { service_provider: serviceProvider } : {}),
    ...(permissions ? { permissions } : {}),
  };

  return applySessionUserDisplayRules(sessionUser);
};

export const buildGhostSessionStorage = ({
  apiSession,
  actingUser,
  tenant = null,
  tenantFlags = null,
}) => {
  const resolvedActingUser = resolveActingUserForGhostStorage(apiSession, actingUser);

  return {
    token: apiSession.token,
    jti: apiSession.jti,
    reason: apiSession.ghost_reason,
    startedAt: apiSession.ghost_started_at,
    expiresAt: apiSession.expires_at,
    spId: apiSession.sp_id,
    spName: tenant?.spName || tenant?.name || null,
    actingUser: resolvedActingUser,
    tenantFlags: tenantFlags || null,
  };
};

export const resolveActingUserForGhostStorage = (apiSession, actingUser = {}) => {
  const apiActingUser = apiSession?.acting_user || {};
  const permissions = normalizeGhostPermissions(
    apiActingUser.permissions ?? actingUser.permissions
  );

  return {
    id: apiActingUser.id ?? actingUser.id,
    uuid: apiActingUser.uuid ?? actingUser.uuid,
    name: apiActingUser.name ?? actingUser.name,
    email: apiActingUser.email ?? actingUser.email,
    user_type: apiActingUser.user_type ?? actingUser.user_type,
    role_id: apiActingUser.role_id ?? actingUser.role_id ?? null,
    ...(apiActingUser.service_provider || actingUser.service_provider
      ? { service_provider: apiActingUser.service_provider ?? actingUser.service_provider }
      : {}),
    ...(permissions ? { permissions } : {}),
  };
};

export const normalizeGhostPermissions = (permissions) => {
  if (!permissions) {
    return null;
  }

  if (!Array.isArray(permissions)) {
    return permissions;
  }

  if (permissions.length === 0) {
    return null;
  }

  return groupByPermissionsByName(permissions);
};

export const resolveRouteUserType = () => {
  const storedUserType = localStorage.getItem("userType");

  if (storedUserType !== USER_ROLE.Super_Admin) {
    return storedUserType;
  }

  const ghostRaw = localStorage.getItem("ghostSession");
  if (ghostRaw) {
    try {
      const ghost = JSON.parse(ghostRaw);
      if (ghost?.actingUser?.user_type) {
        return ghost.actingUser.user_type;
      }
    } catch (e) {
      // ignore malformed ghost session payload
    }
  }

  const tenantRaw = localStorage.getItem("platformTenantContext");
  if (tenantRaw) {
    try {
      const tenant = JSON.parse(tenantRaw);
      if (tenant?.spId != null) {
        return USER_ROLE.Sp_Admin;
      }
    } catch (e) {
      // ignore malformed tenant context payload
    }
  }

  return storedUserType;
};

export const resolveGhostDashboardPath = (userType) => {
  switch (userType) {
    case USER_ROLE.Sp_Standard_User:
      return "/dashboard";
    case "sp_cos":
      return "/";
    default:
      return "/dashboard";
  }
};

export const formatGhostRoleLabel = (userType) => {
  switch (userType) {
    case USER_ROLE.Sp_Admin:
      return "SP Admin";
    case USER_ROLE.Sp_manager:
      return "SP Manager";
    case USER_ROLE.Sp_Standard_User:
      return "Worker";
    case "sp_cos":
      return "Support Coordinator";
    default:
      return userType || "User";
  }
};

export const formatGhostElapsed = (startedAt) => {
  if (!startedAt) {
    return "0:00:00";
  }

  const startedMs = Date.parse(startedAt);
  if (Number.isNaN(startedMs)) {
    return "0:00:00";
  }

  const totalSeconds = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

export const resolveGhostTargetSpId = (user, preferredSpId = null) => {
  if (preferredSpId != null) {
    return preferredSpId;
  }

  const affiliations = user?.affiliations || [];
  if (affiliations.length === 1) {
    return affiliations[0].sp_id;
  }

  return null;
};
